import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { format } from 'date-fns'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertCircle } from 'lucide-react'

import { FilePondUpload } from '@/components/file-pond-upload'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { isAdminUser } from '@/lib/role-guards'
import { useAuth } from '@/features/authentication/components/auth-provider'
import {
  getListRenewalsQueryKey,
  useHandleRenewalApplication,
} from '@/utils/api/membership-service'
import type { SupportingDocumentCreateDTO } from '@/utils/api/membership-service/models'

import { useMemberRenewalStatus } from './hooks/useMemberRenewalStatus'

const DEFAULT_DOCUMENT_TYPE = 'PAYMENT_RECEIPT'

function formatDisplayDate(date: Date | null): string {
  if (!date) return 'Unknown date'
  try {
    return format(date, 'PPP')
  } catch {
    return 'Unknown date'
  }
}

function RenewMembershipSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-4 w-72" />
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
        <CardFooter>
          <Skeleton className="h-10 w-40" />
        </CardFooter>
      </Card>
    </div>
  )
}

export function RenewMembershipPage() {
  const renewalStatus = useMemberRenewalStatus()
  const { user } = useAuth()
  const isAdmin = isAdminUser(user)
  const queryClient = useQueryClient()
  const [documentType, setDocumentType] = useState(DEFAULT_DOCUMENT_TYPE)
  const [description, setDescription] = useState('')
  const [fileDetails, setFileDetails] = useState<{
    fileId: string
    fileUrl: string
    fileName: string
  } | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const formDisabled = isAdmin ? false : !renewalStatus.canRenew

  const renewMutation = useHandleRenewalApplication({
    mutation: {
      onSuccess: async (_data, variables) => {
        toast.success('Renewal request submitted', {
          description: 'We will review your renewal and notify you once it is processed.',
        })

        setFileDetails(null)
        setDescription('')
        setDocumentType(DEFAULT_DOCUMENT_TYPE)
        setFileError(null)

        await Promise.all([
          renewalStatus.refetch(),
          queryClient.invalidateQueries({
            queryKey: getListRenewalsQueryKey(variables.id, { page: 0, size: 50 }),
          }),
        ])
      },
      onError: (error) => {
        const message =
          error instanceof Error ? error.message : 'We could not submit your renewal request.'
        toast.error('Renewal submission failed', { description: message })
      },
    },
  })

  const canSubmit = (isAdmin || renewalStatus.canRenew) && !renewMutation.isPending
  const membershipInfo = renewalStatus.latestApplication
  const membershipSummary = useMemo(() => {
    if (!membershipInfo) {
      return null
    }

    return {
      category: membershipInfo.categoryName ?? 'Uncategorised',
      membershipNumber: membershipInfo.id?.toString() ?? 'Unknown',
      expiresOn: formatDisplayDate(renewalStatus.membershipExpiry),
    }
  }, [membershipInfo, renewalStatus.membershipExpiry])

  if (renewalStatus.isLoading) {
    return <RenewMembershipSkeleton />
  }

  if (!renewalStatus.latestApplication) {
    return (
      <Alert>
        <AlertTitle>No membership application found</AlertTitle>
        <AlertDescription>
          Submit and approve a membership application before you can file renewal requests.
        </AlertDescription>
      </Alert>
    )
  }

  if (renewalStatus.hasPendingRenewal && !isAdmin) {
    return (
      <Alert>
        <AlertTitle>Your renewal application is being worked on</AlertTitle>
        <AlertDescription>
          We’ve received your renewal request and are reviewing it. You’ll be notified once it’s approved.
        </AlertDescription>
        <div className="mt-4">
          <Button variant="outline" asChild>
            <Link to="/membership/renewals">View renewal history</Link>
          </Button>
        </div>
      </Alert>
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!renewalStatus.latestApplication?.id) return

    if (!fileDetails) {
      setFileError('Upload your payment receipt before submitting.')
      return
    }

    const proof: SupportingDocumentCreateDTO = {
      fileId: fileDetails.fileId,
      fileName: fileDetails.fileName,
      fileUrl: fileDetails.fileUrl,
      documentType,
      description: description.trim() ? description.trim() : undefined,
    }

    await renewMutation.mutateAsync({
      id: renewalStatus.latestApplication.id,
      data: {
        categoryName: renewalStatus.latestApplication.categoryName,
        proof,
      },
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Renew membership</h1>
          <p className="text-muted-foreground text-sm">
            Submit your payment receipt to extend your membership term. We&apos;ll review and confirm
            the renewal shortly.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/membership/renewals">View renewal history</Link>
        </Button>
      </div>

      {!renewalStatus.canRenew ? (
        <Alert
          className={cn(
            renewalStatus.hasPendingRenewal
              ? 'border-amber-500/40 bg-amber-500/5 text-amber-900 dark:text-amber-100'
              : 'border-emerald-500/40 bg-emerald-500/5 text-emerald-900 dark:text-emerald-100',
          )}
        >
          {renewalStatus.hasPendingRenewal && <AlertCircle className="h-4 w-4" />}
          <AlertTitle>
            {renewalStatus.hasPendingRenewal ? 'Renewal pending approval' : 'Renewal not available yet'}
          </AlertTitle>
          <AlertDescription>{renewalStatus.reason}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Membership summary</CardTitle>
              <CardDescription>
                Confirm your membership details before submitting the renewal request.
              </CardDescription>
            </div>
            <Badge variant={renewalStatus.membershipExpired ? 'destructive' : 'secondary'}>
              {renewalStatus.membershipExpired ? 'Expired' : 'Active'}
            </Badge>
          </div>

          {membershipSummary && (
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <p className="text-xs uppercase text-muted-foreground">Membership number</p>
                <p className="mt-1 font-medium text-sm">{membershipSummary.membershipNumber}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">Category</p>
                <p className="mt-1 font-medium text-sm">{membershipSummary.category}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">Expired on</p>
                <p className="mt-1 font-medium text-sm">{membershipSummary.expiresOn}</p>
              </div>
            </div>
          )}
        </CardHeader>

        <Separator />

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="document-type">Document type</Label>
              <Select
                value={documentType}
                onValueChange={(value) => {
                  setDocumentType(value)
                }}
                disabled={formDisabled || renewMutation.isPending}
              >
                <SelectTrigger id="document-type" className="sm:max-w-[280px]">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAYMENT_RECEIPT">Payment receipt</SelectItem>
                  <SelectItem value="BANK_SLIP">Bank slip</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="document-description">Notes (optional)</Label>
              <Textarea
                id="document-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Add context about the renewal payment."
                disabled={formDisabled || renewMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label>Upload payment proof</Label>
              <FilePondUpload
                multiple={false}
                onUploadComplete={(fileId, fileUrl, filename) => {
                  setFileDetails({ fileId, fileUrl, fileName: filename })
                  setFileError(null)
                }}
                onRemove={() => {
                  setFileDetails(null)
                }}
                onUploadError={(errorMessage) => {
                  setFileError(errorMessage)
                }}
                disabled={formDisabled || renewMutation.isPending}
                required
              />
              {fileError ? <p className="text-sm text-destructive">{fileError}</p> : null}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              type="submit"
              disabled={!fileDetails || !canSubmit}
              className="sm:w-auto"
            >
              {renewMutation.isPending ? 'Submitting…' : 'Submit renewal request'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
